import { useEffect } from 'react';
import type { RefObject } from 'react';

const TAB_STRIP_ID = 'fortune-sheettab-container-c';

const attachWheelScroll = (strip: HTMLElement): (() => void) => {
    const onWheel = (e: WheelEvent) => {
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (!delta) return;
        const canScroll = delta > 0 ? strip.scrollLeft + strip.clientWidth < strip.scrollWidth : strip.scrollLeft > 0;
        if (!canScroll) return; // let the page/grid handle it — don't hijack wheel events the strip can't use
        e.preventDefault();
        strip.scrollLeft += delta;
    };
    strip.addEventListener('wheel', onWheel, { passive: false });
    return () => strip.removeEventListener('wheel', onWheel);
};

/**
 * Fortune-Sheet's sheet-tab strip (`#fortune-sheettab-container-c`) clips
 * overflow (`white-space: nowrap`) and exposes only fixed-150px prev/next
 * buttons — no prop makes it wheel-scrollable. Attach a non-passive wheel
 * listener directly on the strip so mouse-wheel/trackpad input scrolls it
 * horizontally, preventDefault only when the strip can actually scroll further
 * in that direction so normal page/grid scrolling elsewhere is untouched.
 *
 * The strip is a brand-new DOM node every time the Workbook remounts (its
 * `key` changes) — re-find and re-attach whenever `remountKey` changes. A
 * MutationObserver covers the case where this effect runs before the strip
 * has actually landed in the DOM.
 *
 * `enabled` must be the wrapper's own render gate: the wrapper div doesn't
 * exist while the sheet is still loading, and a ref+key dep alone never
 * re-fires this effect when loading finishes — the listener would silently
 * never attach on a plain open.
 */
export const useSheetTabWheelScroll = (wrapperRef: RefObject<HTMLElement>, remountKey: string | number, enabled: boolean): void => {
    useEffect(() => {
        if (!enabled) return undefined;
        const root = wrapperRef.current;
        if (!root) return undefined;

        let detach: (() => void) | null = null;
        const tryAttach = (): boolean => {
            const strip = root.querySelector<HTMLElement>(`#${TAB_STRIP_ID}`);
            if (!strip) return false;
            detach = attachWheelScroll(strip);
            return true;
        };

        if (tryAttach()) return () => detach?.();

        const observer = new MutationObserver(() => {
            if (tryAttach()) observer.disconnect();
        });
        observer.observe(root, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            detach?.();
        };
    }, [wrapperRef, remountKey, enabled]);
};
